import { type UseMutationResult } from '@tanstack/react-query'
import { ArrowRightIcon, InfoIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { FormDateInputField, FormRadioGroupDS, FormSelectFieldDS, FormStateDropdownField, FormTelephoneInputDS, FormTextFieldDS } from 'components/reusable/form/FormFields'
import { type StateFormData as GetStateFormData } from 'hooks/useGetStateFormData'
import { type StateFormData } from 'hooks/useStateFormMutation'
import {
  Banner,
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  InputBlock,
  TextButton,
  TextInput,
  Tooltip,
  TooltipProvider,
  useBreakpoint
} from 'slab'

import { useOnboarding } from './member_onboarding_v2/OnboardingContext'
import LoadingSpinnerCentered from './reusable/LoadingSpinnerCentered'

type CurrentCoverage = {
  id?: string;
  covered_participant_id: string;
  coverage_type: string;
  coverage_start_date: string;
  coverage_end_date: string;
  carrier_name: string;
  custom_coverage_type?: string;
}

type ColoradoConfigForm = {
  is_employer_reimbursing_premium: string;
  is_employer_using_ichra: string;
  marital_status: string;
  had_small_group_plan: string;
  is_native_family: string;
  has_pediatric_dental_coverage: string;
  has_current_medical_coverage: string;
  will_cancel_current_coverage: string;
  is_enrolled_in_medicaid: string;
  is_enrolled_in_medicare: string;
  medicaid_participant_id?: string;
  medicare_participant_id?: string;
  is_using_hra: string;
  hra_type: string;
  is_replacing_coverage: string;
  is_assisted_application: string;
  use_paperless_notices: string;
  guardian_given_name?: string;
  guardian_family_name?: string;
  guardian_email?: string;
  guardian_address_line1?: string;
  guardian_address_line2?: string;
  guardian_city?: string;
  guardian_county?: string;
  guardian_state?: string;
  guardian_zip?: string;
  guardian_phone?: string;
  race_american_indian_alaska_native?: boolean;
  race_asian_asianamerican?: boolean;
  race_black_africanamerican?: boolean;
  race_hispanic_latino?: boolean;
  race_middle_eastern_north_african?: boolean;
  race_native_hawaiian_pacific_islander?: boolean;
  race_white_european?: boolean;
  race_other?: boolean;
  race_decline_to_answer?: boolean;
  race_other_description?: string | null;
  replacement_coverage_reason?: string;
  replacement_coverage_custom_reason?: string;
  medical_assistance_coverage_type?: string | undefined;
  assistant_given_name?: string;
  assistant_family_name?: string;
  assistant_relationship: string;
  current_coverages?: CurrentCoverage[];
}

const ColoradoForm = ({
  navigateToNextStep,
  navigateToPreviousStep,
  stateFormMutation,
  stateFormData,
  isLoading,
  error,
  refetch,
}: {
  navigateToNextStep?: () => void;
  navigateToPreviousStep?: () => void;
  stateFormMutation: UseMutationResult<void, GrapeAPIErrorType, StateFormData, unknown>;
  stateFormData: GetStateFormData | undefined;
  isLoading: boolean;
  error: GrapeAPIErrorType | null;
  refetch: () => void;
}) => {
  const { onboardingData, additionalParticipants } = useOnboarding()

  const { breakpointIsAtLeast } = useBreakpoint()

  const convertBooleanToStringValue = (value: boolean) => value ? 'true' : 'false'

  const transformCurrentCoverages = (currentCoverages: any[] | undefined) => {
    if (!currentCoverages) {
      return undefined
    }
    return currentCoverages.map((coverage) => ({
      ...coverage,
      coverage_start_date: coverage.coverage_start_date ? coverage.coverage_start_date.split('T')[0] : '',
      coverage_end_date: coverage.coverage_end_date ? coverage.coverage_end_date.split('T')[0] : '',
    }))
  }

  // the model has a default value of true for will_cancel_current_coverage
  // but that is a blocking value that is not always visible to the user, so the user can get stuck if that value
  // is returned as a default. so make sure this value is actually coming from a model with the gated question visible
  const getDefaultWillCancelCurrentCoverage = (stateFormData: GetStateFormData | undefined) => {
    if (stateFormData?.state_config && stateFormData.state_config.has_current_medical_coverage) {
      return convertBooleanToStringValue(stateFormData.state_config.will_cancel_current_coverage)
    }
    return 'true'
  }

  // the default for the form is "single", which can be a confusing user experience
  const getDefaultMaritalStatus = (stateFormData: GetStateFormData | undefined) => {
    if (stateFormData?.state_config?.id) {
      return stateFormData.state_config.marital_status
    }
    return undefined
  }

  // not applicable is not a placeholder value but it's also not stored on the model
  // so we're calculating dynamically if it should be set
  const getDefaultValueForMedicalAssistanceCoverageType = (stateFormData: GetStateFormData | undefined) => {
    if (!stateFormData || !stateFormData.state_config) { return undefined }
    // if the form has a value, return it
    if (stateFormData.state_config.medical_assistance_coverage_type) {
      return stateFormData.state_config.medical_assistance_coverage_type
    }

    // if the form already exists but does not have a value, return "not applicable"
    if (stateFormData.state_config.id) {
      return 'not_applicable'
    }

    // if the form does not exist, return undefined
    return undefined
  }

  const getDefaultValues = () => {
    return {
      is_employer_reimbursing_premium: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.is_employer_reimbursing_premium) : 'true',
      is_employer_using_ichra: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.is_employer_using_ichra) : 'true',
      had_small_group_plan: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.had_small_group_plan) : 'true',
      is_native_family: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.is_native_family) : undefined,
      marital_status: getDefaultMaritalStatus(stateFormData),
      race_american_indian_alaska_native: stateFormData?.state_config?.race_american_indian_alaska_native || undefined,
      race_asian_asianamerican: stateFormData?.state_config?.race_asian_asianamerican || undefined,
      race_black_africanamerican: stateFormData?.state_config?.race_black_africanamerican || undefined,
      race_hispanic_latino: stateFormData?.state_config?.race_hispanic_latino || undefined,
      race_middle_eastern_north_african: stateFormData?.state_config?.race_middle_eastern_north_african || undefined,
      race_native_hawaiian_pacific_islander: stateFormData?.state_config?.race_native_hawaiian_pacific_islander || undefined,
      race_white_european: stateFormData?.state_config?.race_white_european || undefined,
      race_other: stateFormData?.state_config?.race_other || undefined,
      race_other_description: stateFormData?.state_config?.race_other_description || undefined,
      race_decline_to_answer: stateFormData?.state_config?.race_decline_to_answer || undefined,
      has_pediatric_dental_coverage: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.has_pediatric_dental_coverage) : undefined,
      has_current_medical_coverage: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.has_current_medical_coverage) : undefined,
      will_cancel_current_coverage: getDefaultWillCancelCurrentCoverage(stateFormData),
      is_enrolled_in_medicaid: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.is_enrolled_in_medicaid) : 'false',
      medicaid_participant_id: stateFormData?.state_config?.medicaid_participant_id || undefined,
      is_enrolled_in_medicare: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.is_enrolled_in_medicare) : 'false',
      medicare_participant_id: stateFormData?.state_config?.medicare_participant_id || undefined,
      is_using_hra: 'true', // no is not allowed, so not even reading from state config
      hra_type: stateFormData?.state_config?.hra_type || 'ichra',
      is_replacing_coverage: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.is_replacing_coverage) : 'true',
      replacement_coverage_reason: stateFormData?.state_config?.replacement_coverage_reason || undefined,
      replacement_coverage_custom_reason: stateFormData?.state_config?.replacement_coverage_custom_reason || undefined,
      medical_assistance_coverage_type: getDefaultValueForMedicalAssistanceCoverageType(stateFormData),
      is_assisted_application: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.is_assisted_application) : 'false',
      assistant_given_name: stateFormData?.state_config?.assistant_given_name || undefined,
      assistant_family_name: stateFormData?.state_config?.assistant_family_name || undefined,
      assistant_relationship: stateFormData?.state_config?.assistant_relationship || undefined,
      use_paperless_notices: stateFormData?.state_config ? convertBooleanToStringValue(stateFormData.state_config.use_paperless_notices) : 'true',
      current_coverages: transformCurrentCoverages(stateFormData?.state_config?.current_coverages) || [{
        covered_participant_id: '',
        coverage_type: '',
        custom_coverage_type: '',
        coverage_start_date: '',
        coverage_end_date: '',
        carrier_name: '',
      }],
      guardian_given_name: stateFormData?.state_config?.guardian_given_name || '',
      guardian_family_name: stateFormData?.state_config?.guardian_family_name || '',
      guardian_email: stateFormData?.state_config?.guardian_email || '',
      guardian_address_line1: stateFormData?.state_config?.guardian_address_line1 || '',
      guardian_address_line2: stateFormData?.state_config?.guardian_address_line2 || '',
      guardian_city: stateFormData?.state_config?.guardian_city || '',
      guardian_county: stateFormData?.state_config?.guardian_county || '',
      guardian_state: stateFormData?.state_config?.guardian_state || '',
      guardian_zip: stateFormData?.state_config?.guardian_zip || '',
      guardian_phone: stateFormData?.state_config?.guardian_phone || '',
    }
  }

  const { control, register, trigger, reset, handleSubmit, setValue, watch, formState: { errors, isValid } } = useForm<ColoradoConfigForm>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  const { append, remove } = useFieldArray({
    control,
    name: 'current_coverages',
  })

  useEffect(() => {
    reset(getDefaultValues())
  }, [stateFormData])

  useEffect(() => {
    trigger()
  }, [trigger])

  const [submitError, setSubmitError] = useState<string | null>(null)

  const requiresGuardianInfo = stateFormData?.requires_guardian_info

  const isEmployerReimbursingPremium = watch('is_employer_reimbursing_premium') === 'true'
  const employerNotUsingIchra = watch('is_employer_using_ichra') === 'false'
  const didNotHaveSmallGroupPlan = watch('had_small_group_plan') === 'false'
  const hasCurrentMedicalCoverage = watch('has_current_medical_coverage') === 'true'
  const willNotCancelCurrentCoverage = watch('will_cancel_current_coverage') === 'false'
  const isEnrolledInMedicaid = watch('is_enrolled_in_medicaid') === 'true'
  const requiresMedicaidParticipantId = watch('is_enrolled_in_medicaid') === 'true' && watch('medicaid_participant_id') === undefined
  const isEnrolledInMedicare = watch('is_enrolled_in_medicare') === 'true'
  const requiresMedicareParticipantId = watch('is_enrolled_in_medicare') === 'true' && watch('medicare_participant_id') === undefined
  const notUsingHRA = watch('is_using_hra') === 'false'
  const isReplacingCoverage = watch('is_replacing_coverage') === 'true'
  const isAssistedApplication = watch('is_assisted_application') === 'true'
  const hasRaceSetToOther = watch('race_other')
  const currentCoverages = watch('current_coverages')

  const hasFilledOutCurrentCoverages = (currentCoverages || []).filter(
    (coverage) => coverage.covered_participant_id.length > 0 && coverage.coverage_type.length > 0 && coverage.coverage_start_date.length > 0 && coverage.coverage_end_date.length > 0 &&
    (coverage.coverage_type !== 'other' || (coverage.custom_coverage_type?.length || 0) > 0)
  ).length > 0

  const needsReplacementCoverageReason = isReplacingCoverage && !watch('replacement_coverage_reason')
  const needsCustomReplacementCoverageReason = isReplacingCoverage && watch('replacement_coverage_reason') === 'other' && !watch('replacement_coverage_custom_reason')

  const currentCoveragesValid = hasCurrentMedicalCoverage ? hasFilledOutCurrentCoverages : true

  // for races with fields not being watched, unset "decline to say" checkbox
  const possiblyUnsetDeclineToAnswerCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setValue('race_decline_to_answer', undefined)
    }
  }

  // to avoid overwriting the 'watch' value, use useEffect instead of passing in an onChange handler
  useEffect(() => {
    if (hasRaceSetToOther) {
      setValue('race_decline_to_answer', undefined)
    }
  }, [hasRaceSetToOther, setValue])

  useEffect(() => {
    if (!hasCurrentMedicalCoverage) {
      setValue('will_cancel_current_coverage', 'true')
    }
  }, [hasCurrentMedicalCoverage, setValue])

  useEffect(() => {
    if (!isEmployerReimbursingPremium) {
      setValue('is_employer_using_ichra', 'true')
      setValue('had_small_group_plan', 'true')
    }
  }, [isEmployerReimbursingPremium, setValue])

  // if "decline to say" is checked, unset all other race checkboxes
  const possiblyUnsetRaceCheckboxes = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setValue('race_american_indian_alaska_native', undefined)
      setValue('race_asian_asianamerican', undefined)
      setValue('race_black_africanamerican', undefined)
      setValue('race_hispanic_latino', undefined)
      setValue('race_middle_eastern_north_african', undefined)
      setValue('race_native_hawaiian_pacific_islander', undefined)
      setValue('race_white_european', undefined)
      setValue('race_other', undefined)
      setValue('race_other_description', null)
    }
  }

  // tons of conditional logic on this form
  const allFieldsValid = isValid &&
  !employerNotUsingIchra &&
  !notUsingHRA &&
  !requiresMedicaidParticipantId &&
  !requiresMedicareParticipantId &&
  currentCoveragesValid && !willNotCancelCurrentCoverage &&
  !needsReplacementCoverageReason &&
  !needsCustomReplacementCoverageReason

  const member = onboardingData?.member || null
  const fetchedAdditionalParticipants = additionalParticipants?.data || []

  type CoveredParticipantValue = { given_name?: string, family_name?: string, id: string }
  const coveredParticipants: CoveredParticipantValue[] = []
  if (member) {
    coveredParticipants.push({ given_name: member?.given_name, family_name: member?.family_name, id: member?.participant_id || '' })
  }
  if (fetchedAdditionalParticipants) {
    fetchedAdditionalParticipants.forEach((participant) => {
      coveredParticipants.push({ given_name: participant?.given_name, family_name: participant?.family_name, id: participant?.id || '' })
    })
  }

  // Manually add a "Select one" option to the covered participants options
  // This avoids a scenario where the first option looks selected but is not
  // and the form is blocked from being submitted for an unknown reason
  const coveredParticipantsOptions = [
    { label: 'Select one', value: '' },
    ...coveredParticipants.map((participant) => (
      { label: `${participant?.given_name} ${participant?.family_name}`, value: participant?.id || '' }
    ))
  ]

  const formatMedicalAssistanceCoverageType = (medicalAssistanceCoverageType: string | undefined) => {
    if (!medicalAssistanceCoverageType) { return undefined }
    if (medicalAssistanceCoverageType === 'not_applicable') { return undefined } // this isn't a real value, but it's not a placeholder either
    return medicalAssistanceCoverageType
  }

  const onFormSubmit = async (data: ColoradoConfigForm) => {
    try {
      // Clean up custom coverage type if coverage type is not other
      data.current_coverages?.forEach((coverage) => {
        if (coverage.coverage_type !== 'other') {
          coverage.custom_coverage_type = undefined
        }
      })

      // reset current_coverages if will_cancel_current_coverage is false
      if (data.has_current_medical_coverage === 'false' && (data.current_coverages?.length || 0) > 0) {
        data.current_coverages = []
      }

      await stateFormMutation.mutateAsync({
        is_employer_reimbursing_premium: data.is_employer_reimbursing_premium === 'true',
        is_employer_using_ichra: data.is_employer_using_ichra ? data.is_employer_using_ichra === 'true' : undefined,
        had_small_group_plan: data.had_small_group_plan ? data.had_small_group_plan === 'true' : undefined,
        marital_status: data.marital_status,
        is_native_family: data.is_native_family ? data.is_native_family === 'true' : undefined,
        race_american_indian_alaska_native: data.race_decline_to_answer ? false : data.race_american_indian_alaska_native,
        race_asian_asianamerican: data.race_decline_to_answer ? false : data.race_asian_asianamerican,
        race_black_africanamerican: data.race_decline_to_answer ? false : data.race_black_africanamerican,
        race_hispanic_latino: data.race_decline_to_answer ? false : data.race_hispanic_latino,
        race_middle_eastern_north_african: data.race_decline_to_answer ? false : data.race_middle_eastern_north_african,
        race_native_hawaiian_pacific_islander: data.race_decline_to_answer ? false : data.race_native_hawaiian_pacific_islander,
        race_white_european: data.race_decline_to_answer ? false : data.race_white_european,
        race_other: data.race_decline_to_answer ? false : data.race_other,
        race_other_description: data.race_other_description,
        race_decline_to_answer: data.race_decline_to_answer || false,
        has_pediatric_dental_coverage: data.has_pediatric_dental_coverage === 'true',
        has_current_medical_coverage: data.has_current_medical_coverage === 'true',
        will_cancel_current_coverage: data.will_cancel_current_coverage ? data.will_cancel_current_coverage === 'true' : undefined,
        is_enrolled_in_medicaid: data.is_enrolled_in_medicaid === 'true',
        medicaid_participant_id: data.medicaid_participant_id,
        is_enrolled_in_medicare: data.is_enrolled_in_medicare === 'true',
        medicare_participant_id: data.medicare_participant_id,
        is_using_hra: data.is_using_hra === 'true',
        hra_type: data.hra_type,
        is_replacing_coverage: data.is_replacing_coverage === 'true',
        replacement_coverage_reason: data.replacement_coverage_reason,
        replacement_coverage_custom_reason: data.replacement_coverage_custom_reason,
        medical_assistance_coverage_type: formatMedicalAssistanceCoverageType(data.medical_assistance_coverage_type),
        is_assisted_application: data.is_assisted_application === 'true',
        assistant_given_name: data.assistant_given_name,
        assistant_family_name: data.assistant_family_name,
        assistant_relationship: data.assistant_relationship,
        use_paperless_notices: data.use_paperless_notices === 'true',
        current_coverages_attributes: data.current_coverages?.map((coverage) => ({
          covered_participant_id: coverage.covered_participant_id,
          coverage_type: coverage.coverage_type,
          custom_coverage_type: coverage.custom_coverage_type,
          coverage_start_date: coverage.coverage_start_date,
          coverage_end_date: coverage.coverage_end_date,
          carrier_name: coverage.carrier_name,
          _destroy: false,
        })) || undefined,
        guardian_given_name: data.guardian_given_name,
        guardian_family_name: data.guardian_family_name,
        guardian_email: data.guardian_email,
        guardian_address_line1: data.guardian_address_line1,
        guardian_address_line2: data.guardian_address_line2,
        guardian_city: data.guardian_city,
        guardian_county: data.guardian_county,
        guardian_state: data.guardian_state,
        guardian_zip: data.guardian_zip,
        guardian_phone: data.guardian_phone,
      }, {
        onSuccess: () => {
          refetch()
          navigateToNextStep?.()
        },
      })
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An error occurred while submitting the form')
    }
  }

  if (isLoading) {
    return (
      <div>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  if (error) {
    return (
      <div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-md'>
        <Banner
          title='Something went wrong'
          type='critical'
        >
          <p>We couldn't load this page's data. Please try again later.</p>
        </Banner>
      </div>
    )
  }

  const currentCoveragesLength = watch('current_coverages')?.length || 0

  const currentCoverageBlock = (index: number) => (
    <div className='flex flex-col gap-y-3 px-1 py-3'>

      <FormSelectFieldDS<ColoradoConfigForm>
        fieldId={`current_coverages.${index}.covered_participant_id`}
        fieldLabel='Participant'
        fieldOptions={coveredParticipantsOptions}
        register={register}
        error={errors.current_coverages?.[index]?.covered_participant_id}
      />

      <FormTextFieldDS<ColoradoConfigForm>
        fieldId={`current_coverages.${index}.carrier_name`}
        fieldLabel='Insurance company'
        register={register}
        error={errors.current_coverages?.[index]?.carrier_name}
      />

      <FormSelectFieldDS<ColoradoConfigForm>
        fieldId={`current_coverages.${index}.coverage_type`}
        fieldLabel='Coverage type'
        fieldOptions={[
          { label: 'Group Comprehensive Major Medical', value: 'group_comprehensive_major_medical' },
          { label: 'Individual Comprehensive Major Medical', value: 'individual_comprehensive_major_medical' },
          { label: 'Medicare Supplement', value: 'medicare_supplement' },
          { label: 'Hospital Coverage Only', value: 'hospital_coverage_only' },
          { label: 'Vision Coverage Only', value: 'vision_coverage_only' },
          { label: 'Other', value: 'other' },
        ]}
        register={register}
        error={errors.current_coverages?.[index]?.coverage_type}
      />
      {
        watch(`current_coverages.${index}.coverage_type`) === 'other' && (
          <FormTextFieldDS<ColoradoConfigForm>
            fieldId={`current_coverages.${index}.custom_coverage_type`}
            fieldLabel='Other coverage type'
            register={register}
            error={errors.current_coverages?.[index]?.custom_coverage_type}
          />
        )
      }
      <div className='flex flex-row gap-x-1'>
        <div className='flex flex-col gap-x-1'>
          <FormDateInputField<ColoradoConfigForm>
            fieldId={`current_coverages.${index}.coverage_start_date`}
            fieldLabel='Coverage start date'
            watch={watch}
            onSelectDate={(date) => {
              const transformedDate = date ? date.toISOString().split('T')[0] : ''
              setValue(`current_coverages.${index}.coverage_start_date`, transformedDate)
            }}
          />
        </div>
        <div className='flex flex-col gap-x-1'>
          <FormDateInputField<ColoradoConfigForm>
            fieldId={`current_coverages.${index}.coverage_end_date`}
            fieldLabel='Coverage end date'
            watch={watch}
            onSelectDate={(date) => {
              const transformedDate = date ? date.toISOString().split('T')[0] : ''
              setValue(`current_coverages.${index}.coverage_end_date`, transformedDate)
            }}
          />
        </div>
        {
          breakpointIsAtLeast('xs') && (
            <div className='max-w-48 mr-6 ml-auto mt-auto'>
              <Button
                variant='tertiary-destructive'
                onClick={() => remove(index)}
                size='small'
              >
                Remove participant
              </Button>
            </div>)
        }
      </div>
      {!breakpointIsAtLeast('xs') && (
        <div className='flex flex-row gap-x-1 justify-end'>
          <Button
            variant='tertiary-destructive'
            onClick={() => remove(index)}
            size='small'
          >
            Remove participant
          </Button>
        </div>
      )}

    </div>
  )

  const raceLabel = (
    <div className='flex flex-row gap-x-1'>
      <legend className='body-sm-bold text-gray-800 mb-2'>What's your race? (Select all that apply)</legend>
      <TooltipProvider>
        <Tooltip content='This information is confidential and is only used to help improve service to all Coloradans by making sure everyone gets fair access to coverage. Providing this information will not impact eligibility, plan options, or costs.'>
          <InfoIcon className='w-4 h-4 text-gray-500' />
        </Tooltip>
      </TooltipProvider>
    </div>
  )

  function renderButtons () {
    const backButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.()
        }}
      >
        Back
      </Button>
    )

    const nextButton = (
      <Button
        rightIcon={ArrowRightIcon}
        variant='primary'
        type='submit'
        disabled={stateFormMutation.isPending || !allFieldsValid}
      >
        Next
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {backButton}
          {nextButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {nextButton}
        {backButton}
      </ButtonGroup>
    )
  }

  return (
    <div className='flex flex-col gap-y-6'>
      <p className='mb-3 body text-gray-500'>We need to ask the following questions under Colorado state law to determine whether you're eligible for coverage through your employer.</p>
      <form onSubmit={handleSubmit(onFormSubmit)} className='flex flex-col gap-y-6'>
        {/* Employer Eligibility Section */}
        <FormRadioGroupDS<ColoradoConfigForm>
          control={control}
          name='is_employer_reimbursing_premium'
          description='If you work for a small employer, select yes — Thatch is an HRA. If you work for a company with more than 100 employees, select no'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Is your employer considered a small employer (100 or fewer employees)? If so, will your employer be paying for a portion of your premium costs through a health reimbursement arrangement (HRA) or wage adjustment?'
          options={[
            {
              label: 'Yes',
              value: 'true',
            },
            { label: 'No', value: 'false' },
          ]}
        />
        {isEmployerReimbursingPremium && (
          <div className='flex flex-col gap-y-6'>
            <div>
              <FormRadioGroupDS<ColoradoConfigForm>
                control={control}
                name='is_employer_using_ichra'
                buttonVariant='bordered'
                layout='horizontal'
                buttonSize='small'
                label='If your small employer is reimbursing you through an HRA, does that HRA meet the requirements of being a qualified small employer
                  health reimbursement arrangement (QSEHRA) or an individual coverage health reimbursement arrangement (ICHRA)?'
                options={[
                  {
                    label: 'Yes',
                    value: 'true',
                  },
                  { label: 'No', value: 'false' },
                ]}
              />
              {
                employerNotUsingIchra && (
                  <div className='mt-3'>
                    <Banner title='Select Yes — Thatch is an ICHRA.' type='caution' />
                  </div>
                )
              }
            </div>

            <div>
              <FormRadioGroupDS<ColoradoConfigForm>
                control={control}
                name='had_small_group_plan'
                buttonVariant='bordered'
                layout='horizontal'
                buttonSize='small'
                label='Did your small employer offer health insurance benefits in the past 12 months?'
                options={[
                  {
                    label: 'Yes',
                    value: 'true',
                  },
                  { label: 'No', value: 'false' },
                ]}
              />
              {didNotHaveSmallGroupPlan && (
                <div className='mt-3'>
                  <Banner title='You indicated your employer did not offer health insurance benefits in the past 12 months.' type='caution'>
                    Since your employer is reimbursing employees for some portion of their insurance premium but didn't offer a group plan in the past 12 months, you'll need to submit a signed affidavit.
                    <br />
                    Reach out to <a href='mailto:support@thatch.com' className='text-thatch-purple-secondary hover:text-thatch-purple-primary'>support@thatch.com</a> to get the affidavit, or select no if this was an accident.
                  </Banner>
                </div>
              )}
            </div>
          </div>
        )}

        <FormSelectFieldDS<ColoradoConfigForm>
          fieldId='marital_status'
          fieldLabel="What's your marital status?"
          placeholder='Select one'
          fieldOptions={[
            { label: 'Single', value: 'single' },
            { label: 'Married', value: 'married' },
            { label: 'Common law', value: 'common_law' },
            { label: 'Civil union', value: 'civil_union' },
            { label: 'Legally separated', value: 'legally_separated' },
            { label: 'Divorced', value: 'divorced' },
            { label: 'Under 21', value: 'under_21' },
          ]}
          register={register}
          error={errors.marital_status}
          validationRule={{ required: 'Marital status is required' }}
        />

        <FormRadioGroupDS<ColoradoConfigForm>
          control={control}
          name='is_native_family'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Are you or is anyone in your family American Indian or Alaskan Native?'
          options={[
            {
              label: 'Yes',
              value: 'true',
            },
            { label: 'No', value: 'false' },
          ]}
        />

        <CheckboxGroup
          label={raceLabel}
        >
          <Checkbox
            {...register('race_american_indian_alaska_native')}
            label='American Indian or Alaskan Native'
            onChange={possiblyUnsetDeclineToAnswerCheckbox}
          />
          <Checkbox
            {...register('race_asian_asianamerican')}
            label='Asian or Asian American'
            onChange={possiblyUnsetDeclineToAnswerCheckbox}
          />
          <Checkbox
            {...register('race_black_africanamerican')}
            label='Black or African American'
            onChange={possiblyUnsetDeclineToAnswerCheckbox}
          />
          <Checkbox
            {...register('race_hispanic_latino')}
            label='Hispanic or Latino'
            onChange={possiblyUnsetDeclineToAnswerCheckbox}
          />
          <Checkbox
            {...register('race_middle_eastern_north_african')}
            label='Middle Eastern or North African'
            onChange={possiblyUnsetDeclineToAnswerCheckbox}
          />
          <Checkbox
            {...register('race_native_hawaiian_pacific_islander')}
            label='Native Hawaiian or Pacific Islander'
            onChange={possiblyUnsetDeclineToAnswerCheckbox}
          />
          <Checkbox
            {...register('race_white_european')}
            label='White or European'
            onChange={possiblyUnsetDeclineToAnswerCheckbox}
          />
          <Checkbox
            {...register('race_other')}
            label='Not listed or other'
          />
          {watch('race_other') && (
            <div className='ml-6 max-w-48'>
              <TextInput
                {...register('race_other_description')}
                placeholder='Please specify'
                maxLength={256}
              />
            </div>
          )}
          <Checkbox
            {...register('race_decline_to_answer')}
            label='Prefer not to answer'
            onChange={possiblyUnsetRaceCheckboxes}
          />
        </CheckboxGroup>

        <FormRadioGroupDS<ColoradoConfigForm>
          control={control}
          name='has_pediatric_dental_coverage'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Pediatric dental coverage is a required essential health benefit, but the plan you selected might not include pediatric dental coverage. Do you have pediatric dental coverage under a different plan?'
          options={[
            {
              label: 'Yes',
              value: 'true',
            },
            { label: 'No', value: 'false' },
          ]}
        />

        <FormRadioGroupDS<ColoradoConfigForm>
          control={control}
          name='has_current_medical_coverage'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Do you or any of your dependents currently have health coverage?'
          options={[
            {
              label: 'Yes',
              value: 'true',
            },
            { label: 'No', value: 'false' },
          ]}
        />

        {hasCurrentMedicalCoverage && (
          <>
            <div className='flex flex-col gap-y-3'>
              <FormRadioGroupDS<ColoradoConfigForm>
                control={control}
                name='will_cancel_current_coverage'
                buttonVariant='bordered'
                layout='horizontal'
                buttonSize='small'
                label='Are you planning to cancel that current health coverage if this application goes through?'
                options={[
                  {
                    label: 'Yes',
                    value: 'true',
                  },
                  { label: 'No', value: 'false' },
                ]}
              />
              {willNotCancelCurrentCoverage && (
                <div className='mt-3'>
                  <Banner title='You must cancel any current coverage once you enroll in a new plan.' type='caution' />
                </div>
              )}
            </div>

            <div className='flex flex-col gap-y-3 bg-gray-50 p-3 rounded-md border-color-stroke-light border'>
              <div>
                <legend className='body text-gray-800 ml-1 mb-2'>List all participants with current coverage:</legend>
                {Array.from({ length: currentCoverages?.length || 0 }).map((_, index) => (
                  <div key={index}>
                    {currentCoverageBlock(index)}
                  </div>
                ))}
              </div>
              {
                currentCoveragesLength < coveredParticipants.length && (
                  <div className='max-w-48 ml-3 mb-3'>
                    <TextButton
                      color='purple'
                      onClick={() => append({ covered_participant_id: '', coverage_type: '', coverage_start_date: '', coverage_end_date: '', carrier_name: '', custom_coverage_type: '' })}
                    >
                      + Add participant
                    </TextButton>
                  </div>
                )
}
            </div>
          </>
        )}

        <div className='flex flex-col'>
          <FormRadioGroupDS<ColoradoConfigForm>
            control={control}
            name='is_enrolled_in_medicaid'
            buttonVariant='bordered'
            layout='horizontal'
            buttonSize='small'
            label='Are you or anyone you will cover currently enrolled in Medicaid?'
            options={[
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' }
            ]}
          />

          {
            isEnrolledInMedicaid && coveredParticipants.length > 0 && (
              <div className='mt-3 bg-gray-50 p-3 rounded-md border-color-stroke-light border'>
                <FormSelectFieldDS<ColoradoConfigForm>
                  fieldId='medicaid_participant_id'
                  placeholder='Select a participant'
                  fieldLabel='Select the participant currently covered by Medicaid'
                  fieldOptions={coveredParticipantsOptions}
                  register={register}
                  error={errors.medicaid_participant_id}
                />
              </div>
            )
}
        </div>

        <div className='flex flex-col'>
          <FormRadioGroupDS<ColoradoConfigForm>
            control={control}
            name='is_enrolled_in_medicare'
            buttonVariant='bordered'
            layout='horizontal'
            buttonSize='small'
            label='Are you or anyone you will cover currently enrolled in Medicare, CHIP+, or other government-managed health program?'
            options={[
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' }
            ]}
          />

          {isEnrolledInMedicare && (
            <div className='mt-3 bg-gray-50 p-3 rounded-md border-color-stroke-light border'>
              <FormSelectFieldDS<ColoradoConfigForm>
                fieldId='medicare_participant_id'
                placeholder='Select a participant'
                fieldLabel='Select the participant currently covered by Medicare, CHIP+, or other government-managed health program'
                fieldOptions={coveredParticipantsOptions}
                register={register}
                error={errors.medicare_participant_id}
              />
            </div>
          )}
        </div>

        <FormRadioGroupDS<ColoradoConfigForm>
          control={control}
          name='is_using_hra'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Will you be purchasing a plan using a health reimbursement arrangement (HRA)?'
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ]}
        />

        {notUsingHRA && (
          <div className='mt-1'>
            <Banner title='Select Yes — Thatch is an ICHRA.' type='caution' />
          </div>
        )}

        {!notUsingHRA && (
          <FormSelectFieldDS<ColoradoConfigForm>
            fieldId='hra_type'
            fieldLabel='Select the type of reimbursement arrangement you will be using:'
            fieldOptions={[
              { label: 'HRA', value: 'hra' },
              { label: 'ICHRA', value: 'ichra' },
              { label: 'QSEHRA', value: 'qsehra' },
            ]}
            register={register}
            error={errors.hra_type}
          />)}

        <FormRadioGroupDS<ColoradoConfigForm>
          control={control}
          name='is_replacing_coverage'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Do you intend to replace your current health coverage with the plan you will be applying for?'
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ]}
        />

        {isReplacingCoverage && (
          <>
            <FormSelectFieldDS<ColoradoConfigForm>
              fieldId='replacement_coverage_reason'
              fieldLabel="What is the reason you're replacing your current health coverage?"
              placeholder='Select a reason'
              fieldOptions={[
                { label: 'Additional benefits', value: 'additional_benefits' },
                { label: 'Fewer benefits, lower premiums', value: 'fewer_benefits_lower_premiums' },
                { label: 'Same benefits, lower premiums', value: 'same_benefits_lower_premiums' },
                { label: 'Other', value: 'other' },
              ]}
              register={register}
              error={errors.replacement_coverage_reason}
            />
            {watch('replacement_coverage_reason') === 'other' && (
              <TextInput
                {...register('replacement_coverage_custom_reason')}
                placeholder='Enter custom reason'
                maxLength={35}
              />
            )}
            <FormSelectFieldDS<ColoradoConfigForm>
              fieldId='medical_assistance_coverage_type'
              fieldLabel="If you're covered for medical assistance through Health First Colorado,
                are you covered as:"
              fieldOptions={[
                { label: 'Not applicable', value: 'not_applicable' },
                { label: 'Specified Low Income Medicare Beneficiary', value: 'specified_low_income_medicare_beneficiary' },
                { label: 'Qualified Medicare Beneficiary', value: 'qualified_medicare_beneficiary' },
                { label: 'Other Medicaid Medical Benefits', value: 'other_medicaid_medical_benefits' },
              ]}
              register={register}
              error={errors.medical_assistance_coverage_type}
            />
          </>)}

        <div>
          <FormRadioGroupDS<ColoradoConfigForm>
            control={control}
            name='is_assisted_application'
            buttonVariant='bordered'
            layout='horizontal'
            buttonSize='small'
            label='Is someone helping you complete this form?'
            options={[
              { label: 'No', value: 'false' },
              { label: 'Yes', value: 'true' }
            ]}
          />
          {isAssistedApplication && (
            <div className='flex flex-col mt-4'>
              <legend className='body-sm-bold text-gray-800 mb-2'>
                Please provide the name and relationship of the person assisting you with your application.
              </legend>
              <div className='flex flex-row gap-x-4 mt-2'>
                <TextInput
                  {...register('assistant_given_name')}
                  placeholder='First name'
                />
                <TextInput
                  {...register('assistant_family_name')}
                  placeholder='Last name'
                />
                <TextInput
                  {...register('assistant_relationship')}
                  placeholder='Relationship to you'
                />
              </div>
            </div>
          )}
        </div>

        {/* Paperless Notices Section */}

        <FormRadioGroupDS<ColoradoConfigForm>
          control={control}
          name='use_paperless_notices'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Would you like all policy, premium, and other plan notices to go to your provided email address, instead of sent by mail?'
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ]}
        />

        {requiresGuardianInfo && (
          <div>
            <legend className='text-md text-gray-800 ml-1 mb-2'>
              For applicants under 21 years old, please share the following details for a parent or legal guardian:
            </legend>
            <div className='flex flex-row gap-x-4 mt-4'>
              <FormTextFieldDS<ColoradoConfigForm>
                fieldId='guardian_given_name'
                fieldLabel='First name'
                register={register}
                error={errors.guardian_given_name}
              />
              <FormTextFieldDS<ColoradoConfigForm>
                fieldId='guardian_family_name'
                fieldLabel='Last name'
                register={register}
                error={errors.guardian_family_name}
              />
            </div>

            <div className='mt-4'>
              <InputBlock label='Address'>
                <InputBlock.Row>
                  <InputBlock.Input
                    label='Street address'
                    {...register('guardian_address_line1')}
                  />
                </InputBlock.Row>
                <InputBlock.Row>
                  <InputBlock.Input
                    label='Address line 2'
                    {...register('guardian_address_line2')}
                  />
                </InputBlock.Row>
                <InputBlock.Row>
                  <InputBlock.Input
                    label='City'
                    span='3'
                    {...register('guardian_city')}
                  />
                  <InputBlock.Input
                    label='County'
                    span='3'
                    {...register('guardian_county')}
                  />
                  <FormStateDropdownField
                    fieldId='guardian_state'
                    fieldLabel='State'
                    placeholder='Select a state'
                    register={register}
                    error={errors.guardian_state}
                    spanNumber='2'
                  />
                </InputBlock.Row>
                <InputBlock.Row>
                  <InputBlock.Input
                    label='Zip/Postal Code'
                    span='2'
                    maxLength={5}
                    {...register('guardian_zip')}
                  />
                </InputBlock.Row>
              </InputBlock>
            </div>

            <div className='flex flex-row gap-x-4 mt-4'>
              <FormTelephoneInputDS<ColoradoConfigForm>
                customWidth='max-w-40'
                error={errors.guardian_phone}
                disabled={stateFormMutation.isPending}
                fieldId='guardian_phone'
                fieldLabel='Phone number'
                register={register}
                placeholder='(123) 456-7890'
              />
              <FormTextFieldDS<ColoradoConfigForm>
                fieldId='guardian_email'
                fieldLabel='Email'
                register={register}
                error={errors.guardian_email}
              />
            </div>
          </div>
        )}

        {submitError && (
          <div className='my-3'>
            <Banner
              title='Something went wrong'
              type='critical'
            >
              <p>{submitError}</p>
            </Banner>
          </div>
        )}

        {/* Form Actions */}
        <div className={`${submitError ? 'mt-3' : 'mt-6'}`}>
          {renderButtons()}
        </div>
      </form>
    </div>
  )
}

export default ColoradoForm
